import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { supabase } from '../lib/supabase';
import { EventType } from '@azure/msal-browser';

interface UserRole {
  id: string;
  name: string;
  description: string;
}

export const useAuth = () => {
  const { instance, accounts } = useMsal();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isReporteria, setIsReporteria] = useState(false);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkUserRole = useCallback(async (userEmail: string) => {
    try {
      if (userEmail === 'valvarezo@emin.cl') {
        setIsAdmin(true);
        setIsReporteria(false);
        setUserRole({ id: '1', name: 'admin', description: 'Administrador del sistema' });
        setHasAccess(true);
        setError(null);
        return;
      }

      const { data: userRoleData, error: userRoleError } = await supabase
        .from('user_roles')
        .select(`
          id,
          email,
          role_id,
          roles (
            id,
            name,
            description
          )
        `)
        .eq('email', userEmail)
        .single();

      if (userRoleError) {
        if (userRoleError.code === 'PGRST116') {
          throw new Error('Usuario no tiene permisos asignados');
        }
        throw userRoleError;
      }

      if (!userRoleData || !userRoleData.roles) {
        throw new Error('Usuario no tiene un rol asignado');
      }

      const isUserAdmin = userRoleData.roles.name === 'admin';
      const isUserReporteria = userRoleData.roles.name === 'reporteria';

      setIsAdmin(isUserAdmin);
      setIsReporteria(isUserReporteria);
      setUserRole(userRoleData.roles);
      setHasAccess(isUserAdmin || isUserReporteria);
      setError(null);
    } catch (err) {
      console.error('Error checking user role:', err);
      setIsAdmin(false);
      setIsReporteria(false);
      setUserRole(null);
      setHasAccess(false);
      setError(err instanceof Error ? err.message : 'Error al verificar los permisos del usuario');
    }
  }, []);

  const initializeAuth = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let currentAccount = instance.getActiveAccount();
      
      if (!currentAccount && accounts.length > 0) {
        currentAccount = accounts[0];
        instance.setActiveAccount(currentAccount);
      }

      if (!currentAccount) {
        const allAccounts = instance.getAllAccounts();
        if (allAccounts.length > 0) {
          currentAccount = allAccounts[0];
          instance.setActiveAccount(currentAccount);
        }
      }

      if (!currentAccount) {
        throw new Error('No se encontró una sesión activa');
      }

      try {
        await instance.acquireTokenSilent({
          scopes: ["User.Read"],
          account: currentAccount
        });
      } catch (tokenError) {
        console.error('Token error:', tokenError);
        await instance.loginPopup({
          scopes: ["User.Read"]
        });
        currentAccount = instance.getActiveAccount();
        if (!currentAccount) {
          throw new Error('No se pudo obtener la sesión');
        }
      }

      const userEmail = currentAccount.username.toLowerCase();
      await checkUserRole(userEmail);

    } catch (err) {
      console.error('Error initializing auth:', err);
      setIsAdmin(false);
      setIsReporteria(false);
      setUserRole(null);
      setHasAccess(false);
      setError(err instanceof Error ? err.message : 'Error al verificar los permisos del usuario');
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [instance, accounts, checkUserRole]);

  useEffect(() => {
    let mounted = true;

    const handleAuthStateChange = () => {
      if (mounted) {
        initializeAuth();
      }
    };

    const callbackId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && mounted) {
        setTimeout(() => {
          if (mounted) {
            initializeAuth();
          }
        }, 100);
      }
    });

    initializeAuth();

    return () => {
      mounted = false;
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance, initializeAuth]);

  useEffect(() => {
    if (accounts.length > 0) {
      const currentAccount = instance.getActiveAccount();
      if (!currentAccount || currentAccount.homeAccountId !== accounts[0].homeAccountId) {
        instance.setActiveAccount(accounts[0]);
        initializeAuth();
      }
    }
  }, [accounts, instance, initializeAuth]);

  return { isAdmin, isReporteria, userRole, loading, hasAccess, initialized, error };
};